/**
 * Add a skill prompt to the user's active Prompt Lab session.
 *
 * Two modes:
 *   1. Library skill — pass `skillName` only. Driver looks it up in
 *      the firm's Skills library and attaches it with the canonical
 *      content + version metadata (mirrors what the SkillPicker does
 *      when the user clicks a library row).
 *   2. Custom skill — pass `name` + `content` directly. Mirrors the
 *      "Custom Skill" button in SkillPromptSlots.
 *
 * Either mode honors `weight` (default 1.0) and `enabled` (default true).
 * Approval-gated so NIPE can never silently inject content into the
 * user's prompt without an accept click.
 */

import type { NipeTool } from './types';

export interface AddSkillPromptContext {
  getActivePromptSessionId?: () => string | null;
  /** Library mode. Returns `{ ok:false, reason }` if the name doesn't
   * resolve to a library skill, or `{ ok:true, skillPromptId }` on
   * success. The bridge resolves name → row + version internally. */
  addLibrarySkillPromptToSession?: (
    sessionId: string,
    skillName: string,
    weight: number,
    enabled: boolean,
  ) =>
    | { ok: true; skillPromptId: string }
    | { ok: false; reason: string };
  /** Custom mode. Pure-text skill — no library row, no version. */
  addCustomSkillPromptToSession?: (
    sessionId: string,
    name: string,
    content: string,
    weight: number,
    enabled: boolean,
  ) =>
    | { ok: true; skillPromptId: string }
    | { ok: false; reason: string };
}

const MAX_CONTENT_BYTES = 50_000;
const MAX_NAME_LEN = 200;

export const addSkillPromptTool: NipeTool<AddSkillPromptContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'addSkillPrompt',
      description:
        "Attach a skill prompt to the user's active Prompt Lab session. " +
        'Two modes: (1) Library mode — pass `skillName` only (e.g. ' +
        '"redline-differ"); the bridge resolves it against the firm\'s ' +
        'Skills library and attaches the canonical content + version. ' +
        '(2) Custom mode — pass `name` and `content` directly for an ' +
        'ad-hoc one-shot skill. Use library mode when the user names a ' +
        'specific skill ("add the redline skill"); use custom mode when ' +
        "they want something the library doesn't have. Call listSkills " +
        'first if you are unsure whether a name exists.',
      parameters: {
        type: 'object',
        properties: {
          skillName: {
            type: 'string',
            description:
              'Library mode: the skill name to look up (must match a row ' +
              'from listSkills). Omit when using custom mode.',
          },
          name: {
            type: 'string',
            description:
              'Custom mode: display name for the ad-hoc skill. Required ' +
              'when content is provided.',
          },
          content: {
            type: 'string',
            description:
              'Custom mode: prompt text for the ad-hoc skill. Required ' +
              'when name is provided.',
          },
          weight: {
            type: 'number',
            description:
              'Optional weight (default 1.0). Higher = more influence on ' +
              'the composed prompt.',
          },
          enabled: {
            type: 'boolean',
            description:
              'Optional enabled flag (default true). Pass false to attach ' +
              'but keep dormant.',
          },
        },
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: true,
  handler: async (args, ctx) => {
    if (
      !ctx.getActivePromptSessionId ||
      !ctx.addLibrarySkillPromptToSession ||
      !ctx.addCustomSkillPromptToSession
    ) {
      return {
        ok: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const sessionId = ctx.getActivePromptSessionId();
    if (sessionId === null) {
      return {
        ok: false,
        reason: 'No active prompt session yet (Prompt Lab is still booting).',
      };
    }
    const weight = typeof args.weight === 'number' ? args.weight : 1.0;
    const enabled = typeof args.enabled === 'boolean' ? args.enabled : true;

    const skillName = typeof args.skillName === 'string' ? args.skillName.trim() : '';
    const name = typeof args.name === 'string' ? args.name.trim() : '';
    const content = typeof args.content === 'string' ? args.content : '';

    // Library mode wins when skillName is set. If both are passed we
    // still take library mode so the model can't silently overwrite a
    // library lookup with custom content.
    if (skillName.length > 0) {
      return ctx.addLibrarySkillPromptToSession(
        sessionId,
        skillName,
        weight,
        enabled,
      );
    }

    // Custom mode requires both name + content.
    if (name.length === 0 || content.length === 0) {
      return {
        ok: false,
        reason:
          'Provide either `skillName` (library mode) or both `name` and ' +
          '`content` (custom mode).',
      };
    }
    if (name.length > MAX_NAME_LEN) {
      return {
        ok: false,
        reason: `Custom skill name too long (${name.length} > ${MAX_NAME_LEN}).`,
      };
    }
    if (content.length > MAX_CONTENT_BYTES) {
      return {
        ok: false,
        reason: `Custom skill content too long (${content.length} > ${MAX_CONTENT_BYTES}).`,
      };
    }
    return ctx.addCustomSkillPromptToSession(
      sessionId,
      name,
      content,
      weight,
      enabled,
    );
  },
};
